import { injectable } from 'inversify';
import { BaseSuperAgentRequestFactory, RequestSender } from '@studyportals/mb-platform-http-requests/base';
import { IOrganisationSearchFilters, IOrganisationSearchResponse, SearchApiOrganisationClient } from '@studyportals/search-api-interface';
import { EventAggregationService } from '@/platform/EventAggregationService';
import { RequestInitiatedEvent } from '@/platform/events/RequestInitiatedEvent';
import { RequestCompletedEvent } from '@/platform/events/RequestCompletedEvent';
import { DomainApiProjectionOrganisationForSearch } from '@/infrastructure/DomainApiProjectionOrganisationForSearch';
import { OrganisationSearchResult } from './OrganisationSearchResult';
import { OrganisationInfo } from './OrganisationInfo';
import { OrganisationPresenter } from './OrganisationPresenter';
import { IOrganisationCard } from '@studyportals/domain-client';
import { LatencyTracking } from '@/tracking/LatencyTracking';
import { DataStorage } from '@studyportals/data-storage';

@injectable()
export class SearchOrganisationPresenter extends OrganisationPresenter<IOrganisationSearchFilters> {

	private readonly apiClient: SearchApiOrganisationClient;

	public constructor(
		private readonly latencyTracking: LatencyTracking,
		protected readonly eventAggregationService: EventAggregationService,
		protected readonly domainApiProjection: DomainApiProjectionOrganisationForSearch,
		window: Window
	) {

		super(eventAggregationService, domainApiProjection, window);

		this.apiClient = new SearchApiOrganisationClient(
			this.retrieveBaseUrl(),
			new BaseSuperAgentRequestFactory(),
			new RequestSender()
		);
	}

	private retrieveBaseUrl(): string {
		if (this.isExperiment()) {
			return CONFIG.SEARCH_API_URL;
		}
		return CONFIG.EU_SEARCH_API_URL;
	}

	private isExperiment(): boolean {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		const isExperiment = DataStorage.retrieve('Experiment/multiregion_search');
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		return isExperiment?.number === 1;
	}

	public async retrieve(id: string): Promise<OrganisationInfo | null> {
		const organisationForSearch = await this.domainApiProjection.retrieveOrNull(id);
		if (organisationForSearch === null) {
			return null;
		}

		return new OrganisationInfo(
			`${organisationForSearch.getOrganisationId()}`,
			organisationForSearch.getOrganisationVirtualName(),
			organisationForSearch.getOrganisationName()
		);
	}

	public async search(
		filters: IOrganisationSearchFilters,
		resultSize: number,
		resultsOffset = 0
	): Promise<OrganisationSearchResult> {
		this.eventAggregationService.publish(new RequestInitiatedEvent());

		try {
			const response = await this.timeResponse(filters, resultSize, resultsOffset);
			const organisations = this.reconstituteAll(response.organisations);

			return Promise.resolve({
				organisations,
				totalHits: response.hitsTotal,
			});
		} finally {
			this.eventAggregationService.publish(new RequestCompletedEvent());
		}
	}

	private async timeResponse(filters: IOrganisationSearchFilters, size: number, start: number): Promise<IOrganisationSearchResponse<IOrganisationCard>> {
		const startTime = Date.now();
		const result = await this.apiClient.searchCards(filters, size, start);
		const timeTaken = Date.now() - startTime;

		const experimentVariation = this.isExperiment() ? "experiment_1" : "control_0";
		this.latencyTracking.track(timeTaken, experimentVariation);

		return result
	}
}
